import { Route, NavigationGuardNext } from 'vue-router';

/**
 * Routes that use the simple header. These paths should match routes that have setSimpleHeaderGuard.
 */
const SIMPLE_HEADER_ROUTE_PATTERNS = [/^\/corporate-onboarding/, /^\/job-post/, /^\/registration/];

/**
 * Check if a route path uses the simple header
 */
function usesSimpleHeader(path: string): boolean {
  return SIMPLE_HEADER_ROUTE_PATTERNS.some((pattern) => pattern.test(path));
}

/**
 * Clear the use-simple-header meta tag and emit event to notify GlobalNav
 */
export function clearSimpleHeader(): void {
  const existingMetaTag = document.querySelector('meta[name="use-simple-header"]');

  if (existingMetaTag) {
    existingMetaTag.setAttribute('content', 'false');
    // Emit event to notify GlobalNav to update
    window.dispatchEvent(new CustomEvent('simple-header-changed', { detail: { enabled: false } }));
  }
}

/**
 * Guard that sets the use-simple-header meta tag for routes that use the simple header, i.e. job-post
 */
function setSimpleHeaderGuard(to: Route, from: Route, next: NavigationGuardNext): void {
  let existingMetaTag = document.querySelector('meta[name="use-simple-header"]');

  if (!existingMetaTag) {
    existingMetaTag = document.createElement('meta');
    existingMetaTag.setAttribute('name', 'use-simple-header');
    existingMetaTag.setAttribute('content', 'true');
    document.head.appendChild(existingMetaTag);
  } else {
    existingMetaTag.setAttribute('content', 'true');
  }

  // Emit event to notify GlobalNav to update
  window.dispatchEvent(new CustomEvent('simple-header-changed', { detail: { enabled: true } }));

  next();
}

/**
 * Global guard that clears the simple header when navigating to routes that don't use it.
 * This ensures the simple header doesn't persist incorrectly after navigating away from
 * routes that use it (e.g., corporate onboarding, job post, registration).
 */
export function clearSimpleHeaderGuard(to: Route, from: Route, next: NavigationGuardNext): void {
  // Only clear if navigating to a route that doesn't use the simple header
  if (!usesSimpleHeader(to.path)) {
    clearSimpleHeader();
  }
  next();
}

export default setSimpleHeaderGuard;
